/* Angular svg icons service
 *
 * Creates an icon bank at the end of <body> that contains all svg icons, grouped per
 * container (bank). All containers are defined in global Map "sources", which associates a container
 * name (string) to an url that points to the correspondig svg icon stack file and a container
 * promise which, once resolved, returns the DOM element of the corresponding icon container.
 *
 * Loading an icon part of one bank, loads the bank at the end of the document.
 * this should happen only once for document.
 * svg-icon.component render the icon requested from the bank through this service.
 */

import { HttpClient } from '@angular/common/http';
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, map, take } from "rxjs/operators";

export interface IconConfig {
   obs$: Observable<HTMLElement>;
   details: IconDetails;
}

interface IconBank {
   [key: string]: IconDetails;
}

interface IconDetails {
   bankName: "controlCentre" | "common" | "jsPlayer";
   iconGraphic: string;
}

interface SourceBanks {
   [key: string]: { url: string; iconBank$?: Observable<HTMLElement> };
}
@Injectable({
   providedIn: 'root',
})
export class SvgIconService {
   private reportedErrors = new Set();

   private iconBank: IconBank = {
      loading:          { bankName: "controlCentre", iconGraphic: "bufferingAnim" },
      search:           { bankName: "controlCentre", iconGraphic: "search" },
      folder:           { bankName: "controlCentre", iconGraphic: "folder" },
      newFolder:        { bankName: "controlCentre", iconGraphic: "addFolder" },
      diagnostic:       { bankName: "controlCentre", iconGraphic: "stethLeft" },
      home:             { bankName: "controlCentre", iconGraphic: "landingHollow" },
      userGuide:        { bankName: "controlCentre", iconGraphic: "document" },
      training:         { bankName: "controlCentre", iconGraphic: "media" },
      edge:             { bankName: "controlCentre", iconGraphic: "servers" },
      notifications:    { bankName: "controlCentre", iconGraphic: "notification" },
      chat:             { bankName: "controlCentre", iconGraphic: "chatBalloon" },
      status:           { bankName: "controlCentre", iconGraphic: "status" },
      more:             { bankName: "controlCentre", iconGraphic: "bufferingAnim" },
      edgeFolder:       { bankName: "controlCentre", iconGraphic: "folder" },
      publishFolder:    { bankName: "controlCentre", iconGraphic: "folder" },
      source:           { bankName: "controlCentre", iconGraphic: "folder" },
      libraries:        { bankName: "controlCentre", iconGraphic: "twoFolders" },
      recycleNonEmpty:  { bankName: "controlCentre", iconGraphic: "recycleBinFull" },
      recycle:          { bankName: "controlCentre", iconGraphic: "recycleBinEmpty" },
      sources:          { bankName: "controlCentre", iconGraphic: "twoFolders" },
      endpoints:        { bankName: "controlCentre", iconGraphic: "twoFolders" },
      account:          { bankName: "controlCentre", iconGraphic: "production" },
      media:            { bankName: "controlCentre", iconGraphic: "media" },
      timecode:         { bankName: "controlCentre", iconGraphic: "pushpin" },
      upload:           { bankName: "controlCentre", iconGraphic: "arrowInCloud" },
      edit:             { bankName: "controlCentre", iconGraphic: "mediaClip" },
      edl:              { bankName: "controlCentre", iconGraphic: "mediaClip" },
      edgeFile:         { bankName: "controlCentre", iconGraphic: "document" },
      document:         { bankName: "controlCentre", iconGraphic: "document" },
      still:            { bankName: "controlCentre", iconGraphic: "slide" },
      managers:         { bankName: "controlCentre", iconGraphic: "manageManagers" },
      users:            { bankName: "controlCentre", iconGraphic: "manageUsers" },
      arrivals:         { bankName: "controlCentre", iconGraphic: "arrowInCloud" },
      stats:            { bankName: "controlCentre", iconGraphic: "barchart" },
      sites:            { bankName: "controlCentre", iconGraphic: "site" },
      sitegroups:       { bankName: "controlCentre", iconGraphic: "siteGroup" },
      accounts:         { bankName: "controlCentre", iconGraphic: "production" },
      endpoint:         { bankName: "controlCentre", iconGraphic: "servers" },
      service:          { bankName: "controlCentre", iconGraphic: "servers" },
      edgeServer:       { bankName: "controlCentre", iconGraphic: "servers" },
      edgeServers:      { bankName: "controlCentre", iconGraphic: "servers" },
      multicam:         { bankName: "controlCentre", iconGraphic: "multicam" },
      livestreams:      { bankName: "controlCentre", iconGraphic: "streaming" },
      livestream:       { bankName: "controlCentre", iconGraphic: "streamItem" },
      livestreamtype:   { bankName: "controlCentre", iconGraphic: "streamItem" },
      site:             { bankName: "controlCentre", iconGraphic: "site" },
      moreKebab:        { bankName: "common",        iconGraphic: "more_vert" },
      remove:           { bankName: "common",        iconGraphic: "chunkyMinus" },
      add:              { bankName: "common",        iconGraphic: "chunkyPlus" },
      publishing:       { bankName: "common",        iconGraphic: "sharing" },
      editing:          { bankName: "common",        iconGraphic: "chunkyPen" },
      configuration:    { bankName: "common",        iconGraphic: "settingsHollow" },
      delete:           { bankName: "common",        iconGraphic: "chunkyDelete" },
      unknown:          { bankName: "common",        iconGraphic: "questionMark" },
      questionMark:     { bankName: "common",        iconGraphic: "questionMark" },
      sharing:          { bankName: "common",        iconGraphic: "sharing" },
      support:          { bankName: "common",        iconGraphic: "questionMark" },
      zoom:             { bankName: "jsPlayer",      iconGraphic: "zoomInOut" },
      splurgeZoomIn:    { bankName: "jsPlayer",      iconGraphic: "zoomIn" },
      splurgeZoomOut:   { bankName: "jsPlayer",      iconGraphic: "zoomOut" },
      lowResolution:    { bankName: "jsPlayer",      iconGraphic: "resMin" },
      mediumResolution: { bankName: "jsPlayer",      iconGraphic: "resMed" },
      highResolution:   { bankName: "jsPlayer",      iconGraphic: "resMax" },
   };

   private sources: SourceBanks = {
      controlCentre: { url: '/furniture/img/controlCentre-svgIcons.svg' },
      jsPlayer: { url: '/furniture/img/jsPlayer-svgIcons.svg' },
      common: { url: '/furniture/img/common-svgIcons.svg' },
   };

   constructor(private http: HttpClient) {}

   public getIcon(icon: string): IconConfig {
      const iconDetails = this.getIconDetails(icon);
      const src = this.sources[iconDetails.bankName];
      if (!src?.iconBank$) {
         src.iconBank$ = this.http.get(src.url, { responseType: 'text' })
            .pipe(
               catchError(error => {
                  return throwError(error);
               }),
               map(text => {
                  const body = document.querySelector('body');
                  let iconBanks = body.querySelector('#fbdn-icon-bank');

                  if (!iconBanks) {
                     const bank = document.createElement('div');
                     bank.id = 'fbdn-icon-bank';
                     bank.style.display = "none";
                     body.appendChild(bank);
                     iconBanks = bank;
                  }

                  if( !iconBanks.querySelector("#fbdn-" + iconDetails.bankName + "-icon-container") ) {
                     const iconContainer = document.createElement('div');
                     iconContainer.id = "fbdn-" + iconDetails.bankName + "-icon-container";
                     iconContainer.innerHTML = text;
                     iconBanks.appendChild(iconContainer);
                     return iconContainer;
                  }
                  else {
                     return iconBanks.querySelector("#fbdn-" + iconDetails.bankName + "-icon-container");
                  }
               }),
            );
      }
      return {
         obs$: src.iconBank$,
         details: iconDetails,
      };
   }

   private getIconDetails(icon: string): IconDetails {
      if (!(icon in this.iconBank) || !this.iconBank[icon].bankName || !this.sources[this.iconBank[icon].bankName] || !this.iconBank[icon].iconGraphic ) {
         if (!this.reportedErrors.has(icon)) {
            if (icon in this.iconBank) {
               window.console.log("Bad icon '" + icon + "': bank '" + this.iconBank[icon].bankName + "' graphic '"+ this.iconBank[icon].iconGraphic + "'");
            }
            else {
               window.console.log("Icon: '" + icon +"'not in icon banks");
            }
            this.reportedErrors.add(icon);
         }
         return {
            iconGraphic: "hereAndThere",
            bankName : "controlCentre"
         };
      }
      else {
         return {
            iconGraphic: this.iconBank[icon].iconGraphic,
            bankName: this.iconBank[icon].bankName
         };
      }
   }
}

